import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api, useTenants, type Tenant, type UserMeOut, type UserOut } from '../lib/api';
import { dotClass, statusLabel } from '../lib/status';
import { Wordmark } from './Login';

/** Customer-side dashboard. Shown to signed-up users (role='user'). For
 * admin-role principals (legacy single-password admin OR a user promoted
 * to role='admin'), we redirect into the operator app.
 *
 * Until an operator attaches the user to a Client, we show an "awaiting
 * approval" placeholder. Once `client_id` is set, we list the tenants the
 * backend scopes to that client (the /api/tenants endpoint auto-filters
 * by the resolved principal's client_id). */
export default function Dashboard() {
  const [me, setMe] = useState<UserMeOut | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api.userMe();
        if (alive) setMe(d);
      } catch (e: any) {
        if (alive) setErr(e.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err) return <Navigate to="/login" replace />;
  if (!me) return <div className="h-full grid place-items-center text-dim text-sm">Loading…</div>;

  if ('is_admin_session' in me && (me.is_admin_session || me.is_api_token)) {
    return <Navigate to="/tenants" replace />;
  }
  // New SaaS model: operators (and admins) live in the operator UI.
  // The /dashboard placeholder is only kept for legacy role='user' rows.
  if ('role' in me && (me.role === 'admin' || me.role === 'operator')) {
    return <Navigate to="/tenants" replace />;
  }

  const u = me as UserOut;

  return (
    <div className="min-h-full">
      <header className="border-b border-line">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-80 transition">
            <Wordmark className="h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-dim">{u.email}</span>
            <button
              onClick={async () => { await api.logout(); window.location.href = '/'; }}
              className="btn-ghost btn-sm"
            >Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {u.client_id
          ? <TenantsView u={u} />
          : <AwaitingApproval u={u} />}
      </main>
    </div>
  );
}


function TenantsView({ u }: { u: UserOut }) {
  // Backend already filters /api/tenants to the user's client_id when the
  // principal is scoped, so a plain listTenants() call returns just this
  // user's tenants — no client_slug required.
  const { data: tenants, isLoading } = useTenants();
  const [q, setQ] = useState('');

  const filtered = (tenants || []).filter(t =>
    !q || t.target_domain.toLowerCase().includes(q.toLowerCase())
       || t.ga_upn.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="animate-slide-up">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome{u.name ? `, ${u.name.split(' ')[0]}` : ''}.
          </h1>
          <p className="text-sm text-dim mt-1">Your tenants and their provisioning status.</p>
        </div>
        <input
          className="input max-w-xs text-sm"
          placeholder="Search domain or tenant…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      {isLoading && <div className="text-dim text-sm">Loading tenants…</div>}

      {tenants && tenants.length === 0 && (
        <div className="card p-8 text-center">
          <div className="text-sm">No tenants yet.</div>
          <p className="text-xs text-dim mt-2">
            Your operator will appear here once they provision a tenant for you.
          </p>
        </div>
      )}

      {tenants && tenants.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg">
              <tr className="text-left text-[10px] uppercase tracking-wider text-dim">
                <th className="p-3 pl-4">Domain</th>
                <th className="p-3">Tenant user</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => <TenantRow key={t.id} t={t} />)}
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-dim text-xs">No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TenantRow({ t }: { t: Tenant }) {
  return (
    <tr className="border-t border-line/40 hover:bg-white/[0.02] transition">
      <td className="p-3 pl-4">
        <div className="flex items-center gap-2">
          <span className={dotClass(t.status)} />
          <Link to={`/dashboard/tenants/${t.id}`} className="font-mono text-xs hover:text-accent transition">
            {t.target_domain}
          </Link>
        </div>
      </td>
      <td className="p-3 font-mono text-[11px] text-dim">{t.ga_upn}</td>
      <td className="p-3 text-xs">{t.current_phase || statusLabel(t.status)}</td>
      <td className="p-3 text-right">
        <Link to={`/dashboard/tenants/${t.id}`} className="text-xs text-accent hover:underline">
          Open →
        </Link>
      </td>
    </tr>
  );
}


function AwaitingApproval({ u }: { u: UserOut }) {
  return (
    <div className="max-w-3xl">
      <div className="mb-8 animate-slide-up">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full border border-line bg-panel text-xs">
          <span className="dot dot-warn" />
          <span className="text-warn">Awaiting operator approval</span>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight mb-2">
          Welcome{u.name ? `, ${u.name.split(' ')[0]}` : ''}.
        </h1>
        <p className="text-sm text-dim leading-relaxed max-w-xl">
          Your Sendepot account is created. Once an operator attaches you to a client,
          your tenants and inboxes will appear here.
        </p>
      </div>
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-dim">Account</div>
          <span className="pill-dim font-mono">{u.role}</span>
        </div>
        <Row label="Name"  value={u.name || <span className="text-dim">—</span>} />
        <Row label="Email" value={<span className="font-mono text-sm">{u.email}</span>} />
        <Row label="Email verified" value={
          u.email_verified
            ? <span className="pill-ok">Verified</span>
            : <span className="pill-dim">Pending</span>
        } />
        <Row label="Client" value={<span className="text-dim">Not assigned yet</span>} />
        <Row label="Created" value={
          <span className="text-xs text-dim tabular-nums">
            {new Date(u.created_at).toLocaleString()}
          </span>
        } />
      </div>
      <div className="mt-8 text-xs text-dim leading-relaxed">
        Need to provision a tenant immediately? Reach out to your operator. Already an operator? <Link to="/admin" className="text-accent hover:underline">Sign in here</Link>.
      </div>
    </div>
  );
}


function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-line/40 last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-dim">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
